/**
 * Uprawnienia i widoczność zalogowanego z centrum ról (decyzja użytkownika
 * 22.10.2026: „centrum zarządzania rolami i uprawnieniami ról […] kto co
 * widzi na froncie, dostęp tylko admin”; przełącznik `roleCenter`,
 * S-22.10-01) — sam stan, bez zapytań i bez Reacta, żeby czytała go czysta
 * reguła `permissions.ts` i układ menu.
 *
 * Wypełnia go `roleAccessApi.ts` po zalogowaniu (`GET /role-center/me`,
 * w tle — rola systemowa nie czeka, poprawka 23.10.2026). Pusty — przełącznik
 * wyłączony, odczyt się nie udał albo stan innej roli — znaczy: obowiązuje
 * lustro mapy w kodzie (`permissions.ts`), jak dotąd.
 */

/** Odpowiedź API dla zalogowanego — klucze uprawnień i elementów to dane. */
export type RoleAccess = {
  role: string;
  roleName: string;
  /** Klucze uprawnień, które rola faktycznie ma (`FTL_PLANNING` …). */
  permissions: string[];
  /** Elementy interfejsu, których rola nie widzi (`menu.cars`, `order.vehicleSuggestions` …). */
  hiddenElements: string[];
  /** `center` — z bazy; `code` — API liczyło z mapy w kodzie (brak bazy). */
  source: 'center' | 'code';
};

let current: RoleAccess | null = null;
const listeners = new Set<() => void>();

/** Bieżący stan — dla `useSyncExternalStore`. */
export function getRoleAccess(): RoleAccess | null {
  return current;
}

export function subscribeRoleAccess(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setRoleAccess(next: RoleAccess | null): void {
  current = next;
  listeners.forEach((l) => l());
}

/**
 * Stan centrum dla tej roli albo `null` (lustro w kodzie). Stan innej roli
 * nie obowiązuje — chroni przed resztką poprzedniej sesji.
 */
export function roleAccessFor(role: string | null | undefined): RoleAccess | null {
  return role && current && current.role === role ? current : null;
}

/** Odpowiedź API w bezpiecznym kształcie — nieznane pole to brak, nie zgadywanie. */
export function normalizeRoleAccess(data: unknown): RoleAccess | null {
  const d = (data ?? {}) as Record<string, unknown>;
  if (typeof d.role !== 'string' || d.role === '') return null;
  const strings = (v: unknown) =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  return {
    role: d.role,
    roleName: typeof d.roleName === 'string' ? d.roleName : d.role,
    permissions: strings(d.permissions),
    hiddenElements: strings(d.hiddenElements),
    source: d.source === 'center' ? 'center' : 'code',
  };
}
